import { useEffect, useRef, useState } from 'react'
import { waitlistApi } from '../services/api'

const OPEN_EVENT = 'open-beta-modal'

/**
 * Fire this from anywhere to open the BetaModal.
 */
export function openBetaModal() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Self-contained beta waitlist modal.
 * Opens on the `open-beta-modal` window event, closes on ESC or overlay click.
 * POSTs to /api/v1/waitlist via the shared api.ts wrapper.
 */
export default function BetaModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const firstFieldRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const open = () => {
      setStatus('idle')
      setErrorMsg('')
      setIsOpen(true)
    }
    window.addEventListener(OPEN_EVENT, open)
    return () => window.removeEventListener(OPEN_EVENT, open)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
    }
  }, [isOpen])

  const close = () => setIsOpen(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    if (!trimmedName || !trimmedEmail) return
    setStatus('submitting')
    setErrorMsg('')
    try {
      await waitlistApi.join({
        name: trimmedName,
        email: trimmedEmail,
        join_beta: true,
      })
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.')
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="v2-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
      role="dialog"
      aria-modal="true"
      aria-label="Join the ShimmerTech beta"
    >
      <div className="v2-modal-card">
        <button
          type="button"
          className="v2-modal-close"
          onClick={close}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="v2-modal-title">Join the beta</h2>
        <p className="v2-modal-lede">
          We're rolling out access in small batches. Drop your email and we'll
          send a token when your slot opens.
        </p>

        {status === 'success' ? (
          <div className="v2-modal-success">
            ✓ You're on the list. Check your inbox soon.
          </div>
        ) : (
          <form className="v2-modal-form" onSubmit={submit}>
            <div className="v2-modal-field">
              <label className="v2-modal-label" htmlFor="beta-name">Name</label>
              <input
                id="beta-name"
                ref={firstFieldRef}
                className="v2-modal-input"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={status === 'submitting'}
              />
            </div>
            <div className="v2-modal-field">
              <label className="v2-modal-label" htmlFor="beta-email">Email</label>
              <input
                id="beta-email"
                className="v2-modal-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'submitting'}
              />
            </div>
            <button
              type="submit"
              className="v2-modal-submit"
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Joining…' : 'Join the beta'}
            </button>
            {status === 'error' && errorMsg && (
              <div className="v2-modal-error">{errorMsg}</div>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
